import React, { useEffect, useState, useRef, useMemo } from "react";
import { api, formatErr, fmtMoney, fmtInvNum, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CURRENCY_CODES } from "@/lib/currencies";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ScanBarcode, Trash2, Plus, Minus, Printer, Save, Search, ShoppingCart, Pencil, Camera, LayoutGrid, RotateCcw, ListOrdered, Wallet } from "lucide-react";
import { CategoryIcon } from "@/components/CategoryIconPicker";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import BarcodeScannerDialog from "@/components/BarcodeScannerDialog";
import SplitPaymentDialog from "@/pages/sales/components/SplitPaymentDialog";
import ProductPickerPanel from "@/pages/sales/components/ProductPickerPanel";
import { useNavigate, useSearchParams } from "react-router-dom";

// Return kinds are the "opposite" side of a normal invoice
const isReturnKind = (k) => k === "sale_return" || k === "purchase_return";
const isSaleSide = (k) => k === "sale" || k === "sale_return";
const kindTitle = (k, isEdit, existing) => {
  const map = {
    sale: "فاتورة بيع",
    purchase: "فاتورة شراء",
    sale_return: "مرتجع مبيعات",
    purchase_return: "مرتجع مشتريات",
  };
  return isEdit ? `تعديل ${map[k]} #${existing?.number}` : map[k];
};

export default function InvoiceEditor({ kind = "sale", existing = null, onSaved }) {
  const { currentCompany, user } = useAuth();
  const { t } = useI18n();
  const isEdit = !!existing;
  const nav = useNavigate();
  const [searchParams] = useSearchParams();

  const partyLabel = isSaleSide(kind) ? "العميل" : "المورد";
  const partyEndpoint = isSaleSide(kind) ? "customers" : "suppliers";
  const defaultParty = isSaleSide(kind) ? "عميل نقدي" : "مورد نقدي";

  const [lines, setLines] = useState(existing?.lines || []);
  const [barcodeVal, setBarcodeVal] = useState("");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [parties, setParties] = useState([]);
  const [partyName, setPartyName] = useState(existing?.party_name || defaultParty);
  const [partyId, setPartyId] = useState(existing?.party_id || null);
  const [payment, setPayment] = useState(existing?.payment_type || "cash");
  const [paidAmount, setPaidAmount] = useState(existing?.paid_amount ?? 0);
  const [discountPercent, setDiscountPercent] = useState(existing?.discount_percent ?? 0);
  const [discountAmount, setDiscountAmount] = useState(existing?.discount_amount ?? 0);
  const [notes, setNotes] = useState(existing?.notes || "");
  const [supplierInvNumber, setSupplierInvNumber] = useState(existing?.supplier_invoice_number || "");
  const [currency, setCurrency] = useState(existing?.currency || currentCompany?.base_currency || "SYP");
  const [rate, setRate] = useState(existing?.exchange_rate || 1);
  const [camOpen, setCamOpen] = useState(false);
  const [mobilePanel, setMobilePanel] = useState("products");
  const scanRef = useRef(null);
  const [cashBoxes, setCashBoxes] = useState([]);
  const [cashBoxId, setCashBoxId] = useState(existing?.cash_box_id || user?.default_cash_box_id || "");
  const [splitOpen, setSplitOpen] = useState(false);
  // splits: [{cash_box_id, currency, amount}]
  const [splits, setSplits] = useState(existing?.payments || []);
  const [returnReason, setReturnReason] = useState(existing?.return_reason || "");
  const [invoiceTemplate, setInvoiceTemplate] = useState({ invoice_layout: "thermal" });
  const [returnSource, setReturnSource] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState(existing?.warehouse_id || "");
  const [costCenters, setCostCenters] = useState([]);
  const [costCenter, setCostCenter] = useState(existing?.cost_center || "");
  const [vatRateOverride, setVatRateOverride] = useState(existing?.vat_rate ?? "");
  // Advanced options collapsed by default — kept open if the invoice already uses advanced fields
  const [showAdvanced, setShowAdvanced] = useState(
    !!(existing && (existing.discount_percent || existing.discount_amount || existing.exchange_rate !== 1 || existing.currency !== "SYP" || existing.cash_box_id || (existing.payments && existing.payments.length)))
  );
  const [draftHydrated, setDraftHydrated] = useState(isEdit);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const draftKey = `muhasabaty_invoice_draft_${currentCompany?.id || "none"}_${kind}`;

  const loadProducts = async () => { try { const { data } = await api.get("/products"); setProducts(data); } catch{} };
  const [rates, setRates] = useState({});
  const loadRates = async () => { try { const { data } = await api.get("/currencies"); setRates(data?.rates || {}); } catch{} };
  const invBase = (currentCompany?.base_currency || "SYP").toUpperCase();
  const currencyOptions = (() => {
    const set = new Set();
    if (currentCompany?.base_currency) set.add(currentCompany.base_currency.toUpperCase());
    (currentCompany?.enabled_currencies || []).forEach(c => c && set.add(c.toUpperCase()));
    Object.keys(rates || {}).forEach(c => c && set.add(c.toUpperCase()));
    if (!set.size) set.add("SYP");
    return Array.from(set);
  })();
  // rates[X] = value of 1 unit of X in base currency
  const convertAmount = (amount, fromCur, toCur) => {
    const src = (fromCur || invBase).toUpperCase();
    const dst = (toCur || invBase).toUpperCase();
    if (src === dst) return Number(amount || 0);
    const sr = src === invBase ? 1 : Number(rates[src] || 0);
    const dr = dst === invBase ? 1 : Number(rates[dst] || 0);
    if (!sr || !dr) return Number(amount || 0);
    return Number(amount || 0) * sr / dr;
  };
  const loadCategories = async () => { try { const { data } = await api.get("/categories"); setCategories(data); } catch{} };
  const loadParties = async () => { try { const { data } = await api.get(`/${partyEndpoint}`); setParties(data); } catch{} };
  const loadCashBoxes = async () => { try { const { data } = await api.get("/cashboxes"); setCashBoxes(data); } catch{} };
  const loadWarehouses = async () => { try { const { data } = await api.get("/warehouses"); setWarehouses(data); } catch{} };
  const loadCostCenters = async () => {
    try {
      const { data } = await api.get("/cost-centers");
      const list = data || [];
      setCostCenters(list);
      const locked = user?.branch_cost_center || "";
      if (locked && !isEdit && list.some(c => c.name === locked)) {
        setCostCenter(locked);
        const cc = list.find(c => c.name === locked);
        if (cc?.warehouse_id) setWarehouseId(cc.warehouse_id);
      }
    } catch {}
  };
  useEffect(() => { loadProducts(); loadCategories(); loadParties(); loadCashBoxes(); loadWarehouses(); loadCostCenters(); loadRates(); if (!isEdit) scanRef.current?.focus(); }, [kind]); // eslint-disable-line
  useEffect(() => { api.get("/company/template").then(({ data }) => setInvoiceTemplate(data || {})).catch(() => {}); }, [currentCompany?.id]);

  // Restore an unfinished invoice after refresh or a brief outage.
  useEffect(() => {
    if (isEdit || !currentCompany?.id) { setDraftHydrated(true); return; }
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        const d = JSON.parse(raw);
        if (d.lines?.length || d.party_name || d.notes) {
          setLines(d.lines || []); setPartyName(d.party_name || defaultParty); setPartyId(d.party_id || null);
          setPayment(d.payment || "cash"); setPaidAmount(d.paid_amount ?? 0);
          setDiscountPercent(d.discount_percent ?? 0); setDiscountAmount(d.discount_amount ?? 0);
          setNotes(d.notes || ""); setCurrency(d.currency || currency); setRate(d.rate || 1);
          setSupplierInvNumber(d.supplier_invoice_number || ""); setCashBoxId(d.cash_box_id || "");
          setSplits(d.splits || []); setWarehouseId(d.warehouse_id || ""); setCostCenter(d.cost_center || "");
          setVatRateOverride(d.vat_rate ?? ""); setDraftSavedAt(d.saved_at || null);
          toast.info("تم استرجاع مسودة الفاتورة المحفوظة تلقائياً");
        }
      }
    } catch {}
    setDraftHydrated(true);
  }, [currentCompany?.id, kind, isEdit]); // eslint-disable-line

  // Debounced local draft; it works independently of the server and offline queue.
  useEffect(() => {
    if (isEdit || !draftHydrated || !currentCompany?.id) return;
    const hasContent = lines.length > 0 || partyName !== defaultParty || notes || supplierInvNumber;
    const timer = setTimeout(() => {
      if (!hasContent) { localStorage.removeItem(draftKey); setDraftSavedAt(null); return; }
      const saved_at = new Date().toISOString();
      localStorage.setItem(draftKey, JSON.stringify({ lines, party_name: partyName, party_id: partyId, payment,
        paid_amount: paidAmount, discount_percent: discountPercent, discount_amount: discountAmount, notes,
        currency, rate, supplier_invoice_number: supplierInvNumber, cash_box_id: cashBoxId, splits,
        warehouse_id: warehouseId, cost_center: costCenter, vat_rate: vatRateOverride, saved_at }));
      setDraftSavedAt(saved_at);
    }, 700);
    return () => clearTimeout(timer);
  }, [draftHydrated, currentCompany?.id, kind, isEdit, lines, partyName, partyId, payment, paidAmount, discountPercent, discountAmount, notes, currency, rate, supplierInvNumber, cashBoxId, splits, warehouseId, costCenter, vatRateOverride]); // eslint-disable-line

  // Re-price all cart lines whenever the invoice currency or the rate map changes.
  // Each line keeps its native source_price/source_currency so conversions never compound.
  useEffect(() => {
    setLines(ls => ls.map(l => {
      const sc = (l.source_currency || currency).toUpperCase();
      const sp = l.source_price != null ? Number(l.source_price) : Number(l.price || 0);
      const np = Math.round(convertAmount(sp, sc, currency) * 100) / 100;
      return { ...l, price: np, total: l.quantity * np - (l.discount || 0) };
    }));
  }, [currency, rates]); // eslint-disable-line

  // Prefill from source invoice when ?from=<invoice_id> present (for creating a return from an original invoice)
  useEffect(() => {
    const fromId = searchParams.get("from");
    if (!fromId || isEdit || !isReturnKind(kind)) return;
    (async () => {
      try {
        const { data: src } = await api.get(`/invoices/${fromId}`);
        if (!src) return;
        setReturnSource(src);
        setLines(src.lines || []);
        setPartyId(src.party_id || null);
        setPartyName(src.party_name || defaultParty);
        setCurrency(src.currency || "SYP");
        setRate(src.exchange_rate || 1);
        setNotes(`مرتجع من الفاتورة #${src.number}${src.notes ? " · " + src.notes : ""}`);
        toast.success(`تم تحميل قالب "مرتجع كامل" من الفاتورة #${src.number} — عدّل الكميات إن لزم`);
      } catch (e) { toast.error(formatErr(e)); }
    })();
  }, [searchParams, kind, isEdit]); // eslint-disable-line

  // Apply "Full Return" template: reset every line quantity to the original invoice's line quantity
  const applyFullReturnTemplate = () => {
    if (!returnSource?.lines?.length) return toast.info("لا توجد فاتورة أصلية للاستيراد");
    setLines(returnSource.lines.map(l => ({ ...l, total: l.quantity * l.price - (l.discount || 0) })));
    toast.success(`تم استعادة الكميات الأصلية للفاتورة #${returnSource.number}`);
  };

  // Keyboard shortcuts: F2 = focus barcode, F3 = focus search, F9 = save
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "F2") { e.preventDefault(); scanRef.current?.focus(); scanRef.current?.select?.(); }
      else if (e.key === "F3") { e.preventDefault(); document.querySelector('[data-testid="product-search-input"]')?.focus(); }
      else if (e.key === "F9" && !e.repeat) { e.preventDefault(); document.querySelector('[data-testid="save-invoice-button"]')?.click?.(); }
      else if (e.key === "Escape") { setBarcodeVal(""); setSearchTerm(""); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const addLine = async (p) => {
    if (!p) return;
    // Auto-defaults on first item: cash + SYP + fully paid — owner can override any time
    if (!isEdit && lines.length === 0) {
      setPayment("cash");
      setCurrency("SYP");
      setPaidAmount(0);
      setRate(1);
    }
    const unit = (p.units && p.units[0]) || { name: "قطعة", factor: 1 };
    const prodCur = (p.currency || invBase).toUpperCase();
    let price = isSaleSide(kind) ? p.sale_price : p.cost_price;
    let priceCur = prodCur;  // currency that `price` is currently expressed in
    // Smart defaults: fetch last-price for this party+product combo (best effort, non-blocking failures)
    if (!isEdit && !isReturnKind(kind)) {
      try {
        const q = new URLSearchParams({ kind: isSaleSide(kind) ? "sale" : "purchase" });
        if (partyId) q.set("party_id", partyId);
        const { data: sd } = await api.get(`/products/${p.id}/smart-defaults?${q.toString()}`);
        // Party-specific last price wins over default catalog price
        if (partyId && sd?.last_price != null && sd.last_price > 0) {
          price = sd.last_price;
          priceCur = currency;  // historical last price is already in the invoice currency
          toast.info(`استُخدم آخر سعر لهذا ${partyLabel}: ${price.toLocaleString("ar-EG")}`);
        }
        // First line + no party → suggest frequent party
        if (!partyId && lines.length === 0 && sd?.frequent_party) {
          setPartyId(sd.frequent_party.id);
          setPartyName(sd.frequent_party.name);
          toast.info(`اقتُرح ${partyLabel}: ${sd.frequent_party.name}`);
        }
      } catch { /* silent - smart-defaults are optional */ }
    }
    const existingIdx = lines.findIndex(l => l.product_id === p.id && l.unit_name === unit.name);
    if (existingIdx >= 0) {
      setLines(ls => ls.map((l,i)=> i===existingIdx ? {...l, quantity:l.quantity+1, total:(l.quantity+1)*l.price - l.discount} : l));
    } else {
      const dispPrice = Math.round(convertAmount(price, priceCur, currency) * 100) / 100;
      setLines(ls => [...ls, { product_id:p.id, product_name:p.name, barcode:p.barcode, unit_name:unit.name, unit_factor:unit.factor, quantity:1, price: dispPrice, source_price: price, source_currency: priceCur, discount:0, total: dispPrice }]);
    }
    setBarcodeVal(""); setSearchTerm(""); scanRef.current?.focus();
  };

  const onScan = async (e) => {
    e.preventDefault();
    if (!barcodeVal.trim()) return;
    try {
      const { data } = await api.get(`/products/barcode/${barcodeVal.trim()}`);
      addLine(data);
    } catch { toast.error("لم يتم العثور على الصنف"); setBarcodeVal(""); scanRef.current?.focus(); }
  };

  const updateLine = (i, patch) => {
    setLines(ls => ls.map((l, idx) => {
      if (idx !== i) return l;
      const merged = {...l, ...patch};
      merged.quantity = Math.max(0, +merged.quantity || 0);
      merged.price = +merged.price || 0;
      merged.discount = +merged.discount || 0;
      merged.total = merged.quantity * merged.price - merged.discount;
      return merged;
    }));
  };

  const subtotal = lines.reduce((s, l) => s + l.total, 0);
  const discPct = +discountPercent || 0;
  const discAmt = +discountAmount || 0;
  const discountTotal = subtotal * discPct / 100 + discAmt;
  const netBeforeVat = Math.max(0, subtotal - discountTotal);
  const companyVatRate = +(currentCompany?.vat_rate || 0);
  const effectiveVatRate = vatRateOverride === "" ? companyVatRate : (+vatRateOverride || 0);
  const vatAmount = +(netBeforeVat * effectiveVatRate / 100).toFixed(2);
  const total = +(netBeforeVat + vatAmount).toFixed(2);
  const effectivePaid = payment === "cash" && (!paidAmount || +paidAmount === 0) ? total : +paidAmount || 0;
  const remaining = +(total - effectivePaid).toFixed(2);

  const filteredProducts = useMemo(() => {
    const t = (searchTerm || "").toLowerCase();
    return products.filter(p => {
      if (activeCat !== "all") {
        const cats = p.categories && p.categories.length ? p.categories : (p.category ? [p.category] : []);
        if (!cats.includes(activeCat) && !(activeCat === "" && cats.length === 0)) return false;
      }
      if (!t) return true;
      return p.name.toLowerCase().includes(t) || (p.barcode || "").includes(searchTerm);
    }).slice(0, 60);
  }, [products, activeCat, searchTerm]);

  // count products per category for the tabs badges (a product with N tags counts N times)
  const catCounts = useMemo(() => {
    const m = { all: products.length, "": 0 };
    for (const p of products) {
      const cats = p.categories && p.categories.length ? p.categories : (p.category ? [p.category] : []);
      if (cats.length === 0) { m[""] = (m[""] || 0) + 1; continue; }
      for (const k of cats) m[k] = (m[k] || 0) + 1;
    }
    return m;
  }, [products]);

  const filteredParties = parties.filter(p => !partyName || p.name.toLowerCase().includes((partyName||"").toLowerCase())).slice(0, 8);

  /** Soft two-tone chime using Web Audio — no external assets needed. */
  const playChime = () => {
    if (localStorage.getItem("mute_chime") === "1") return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const notes = [
        { freq: 880, start: 0.0, dur: 0.12 },
        { freq: 1320, start: 0.09, dur: 0.18 },
      ];
      notes.forEach(({ freq, start, dur }) => {
        const osc = ctx.createOscillator(), gain = ctx.createGain();
        osc.type = "sine"; osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, ctx.currentTime + start);
        gain.gain.exponentialRampToValueAtTime(0.22, ctx.currentTime + start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
        osc.connect(gain); gain.connect(ctx.destination);
        osc.start(ctx.currentTime + start);
        osc.stop(ctx.currentTime + start + dur + 0.05);
      });
      setTimeout(() => ctx.close(), 500);
    } catch {}
  };

  const save = async (print = false) => {
    if (lines.length === 0) return toast.error("أضف صنف واحد على الأقل");
    // Validate splits if used
    const useSplits = splits.length > 0 && splits.some(s => +s.amount > 0);
    if (useSplits) {
      const splitTotal = splits.reduce((s, x) => s + (+x.amount || 0), 0);
      // splitTotal is in mixed currencies — we allow it, backend records each payment on its own box
      if (splitTotal <= 0) return toast.error("أدخل مبالغ للدفعات المقسّمة");
    }
    try {
      const idempotencyKey = (window.crypto?.randomUUID && window.crypto.randomUUID()) || `inv-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const body = {
        kind, party_id: partyId, party_name: partyName || defaultParty,
        payment_type: payment,
        paid_amount: useSplits
          // paid_amount stays in invoice currency: sum only splits matching invoice currency
          ? splits.filter(s => s.currency === currency).reduce((s, x) => s + (+x.amount || 0), 0)
          : (+paidAmount || 0),
        discount_percent: discPct, discount_amount: discAmt,
        currency, exchange_rate: +rate || 1, lines, notes,
        supplier_invoice_number: kind === "purchase" ? (supplierInvNumber || "") : "",
        cash_box_id: useSplits ? null : (cashBoxId || null),
        payments: useSplits ? splits.filter(s => +s.amount > 0).map(s => ({
          cash_box_id: s.cash_box_id || null,
          currency: s.currency,
          amount: +s.amount || 0,
        })) : null,
        return_reason: isReturnKind(kind) ? (returnReason || null) : null,
        ref_invoice_id: returnSource?.id || existing?.ref_invoice_id || null,
        warehouse_id: warehouseId || null,
        cost_center: isSaleSide(kind) ? (costCenter || "") : "",
        vat_rate: vatRateOverride === "" ? null : Number(vatRateOverride),
        idempotency_key: isEdit ? undefined : idempotencyKey,
      };
      const { data } = isEdit
        ? await api.put(`/invoices/${existing.id}`, body).then(async () => { const r = await api.get(`/invoices/${existing.id}`); return r; })
        : await api.post("/invoices", body);
      playChime();
      toast.success(isEdit ? "تم تحديث الفاتورة" : `تم حفظ الفاتورة رقم ${data.display_no || data.number}`);
      if (!isEdit) { localStorage.removeItem(draftKey); setDraftSavedAt(null); }
      if (print) printInvoice(data);
      if (isEdit && onSaved) return onSaved();
      setLines([]); setBarcodeVal(""); setPartyName(defaultParty); setPartyId(null); setPayment("cash"); setPaidAmount(0); setDiscountPercent(0); setDiscountAmount(0); setNotes("");
      setCashBoxId(""); setSplits([]);
      loadProducts();
    } catch(e){
      // Offline: the invoice is safely queued and will sync when the connection returns.
      if (e?.code === "OFFLINE_QUEUED") {
        playChime();
        toast.success("تم حفظ الفاتورة محلياً — ستتم مزامنتها تلقائياً عند عودة الإنترنت. يمكنك متابعة إدخال فواتير أخرى.");
        localStorage.removeItem(draftKey); setDraftSavedAt(null);
        setLines([]); setBarcodeVal(""); setPartyName(defaultParty); setPartyId(null); setPayment("cash"); setPaidAmount(0); setDiscountPercent(0); setDiscountAmount(0); setNotes("");
        setCashBoxId(""); setSplits([]);
        return;
      }
      toast.error(formatErr(e));
    }
  };

  const printInvoice = async (inv) => {
    // For VAT-enabled invoices, fetch ZATCA-compliant TLV base64 payload
    let zatcaB64 = null;
    if (inv.vat_amount > 0 && inv.id) {
      try {
        const { data } = await api.get(`/invoices/${inv.id}/zatca-qr`);
        zatcaB64 = data.zatca_base64;
      } catch { /* silent - fallback to plain QR */ }
    }
    const w = window.open("", "_blank", "width=350,height=600");
    const rows = inv.lines.map(l => `<tr><td>${l.product_name}</td><td>${l.quantity}</td><td>${l.price}</td><td>${l.total}</td></tr>`).join("");
    const barcodeVal = fmtInvNum(inv.number);
    const base = (currentCompany?.base_currency || "SYP").toUpperCase();
    const cur = (inv.currency || "SYP").toUpperCase();
    const layout = invoiceTemplate.invoice_layout || "thermal";
    const fx = Number(inv.exchange_rate) || 1;
    const dual = cur !== base && fx > 0;
    const nf = (n) => Number(n || 0).toLocaleString("ar-EG", { maximumFractionDigits: 2 });
    const design = invoiceTemplate.invoice_design?.[layout] || {};
    const blockOrder = Array.isArray(design.order) ? design.order : ["logo", "company", "header", "invoice", "details", "barcode", "items", "totals", "notes", "terms", "footer"];
    const hiddenBlocks = Array.isArray(design.hidden) ? design.hidden : [];
    const blockStyles = design.blockStyles || {};
    const sectionGap = Math.max(0, Math.min(40, Number(design.spacing ?? 10)));
    const block = (id, html) => {
      const cfg = blockStyles[id] || {};
      const align = ["left", "center", "right"].includes(cfg.align) ? cfg.align : "right";
      const size = Math.max(8, Math.min(32, Number(cfg.fontSize || design.fontSize || 12)));
      return `<section class="designer-block" style="text-align:${align};font-size:${size}px;margin-bottom:${sectionGap}px">${html}</section>`;
    };
    const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
    const logoSrc = invoiceTemplate.logo_url ? (invoiceTemplate.logo_url.startsWith("http") ? invoiceTemplate.logo_url : BACKEND + invoiceTemplate.logo_url) : "";
    const sections = {
      logo: logoSrc ? block("logo", `<img src="${logoSrc}" alt="logo" style="max-width:${Math.max(30, Math.min(500, Number(design.logoWidth || 150)))}px;max-height:${Math.max(30, Math.min(500, Number(design.logoHeight || 70)))}px;object-fit:contain" />`) : "",
      company: block("company", `<strong>${currentCompany?.name || "فاتورة"}</strong>${currentCompany?.address ? `<div>${currentCompany.address}</div>` : ""}${currentCompany?.phone ? `<div>هاتف: ${currentCompany.phone}</div>` : ""}${invoiceTemplate.show_tax_id && (invoiceTemplate.tax_id || currentCompany?.vat_number) ? `<div>الرقم الضريبي: ${invoiceTemplate.tax_id || currentCompany?.vat_number}</div>` : ""}`),
      header: invoiceTemplate.header_text ? block("header", invoiceTemplate.header_text) : "",
      invoice: block("invoice", `${kindTitle(kind, false, null)} رقم ${invoiceTemplate.invoice_prefix || ""}${inv.display_no || fmtInvNum(inv.number)}`),
      details: block("details", `<div>${new Date(inv.created_at).toLocaleString("ar-EG")}</div><div>${partyLabel}: ${inv.party_name}</div><div>نوع الدفع: ${inv.payment_type === "cash" ? "نقدي" : "آجل"}</div>${inv.supplier_invoice_number ? `<div>رقم فاتورة المورد: ${inv.supplier_invoice_number}</div>` : ""}`),
      barcode: block("barcode", `<div class="scan-block"><div class="options"><button type="button" id="mode-barcode" class="toggle-btn active" onclick="setMode('barcode')">Barcode</button><button type="button" id="mode-qr" class="toggle-btn" onclick="setMode('qr')">QR</button></div><svg id="inv-barcode"></svg><canvas id="inv-qr" style="display:none;width:80px;height:80px"></canvas><div style="font-size:9px;color:#666;margin-top:2px">امسح لإنشاء مرتجع</div></div>`),
      items: block("items", `<table><thead><tr><th>الصنف</th><th>كمية</th><th>سعر</th><th>الإجمالي</th></tr></thead><tbody>${rows}</tbody></table>`),
      totals: block("totals", `<div class="row"><span>الفرعي</span><span>${inv.subtotal?.toLocaleString("ar-EG") || 0}</span></div>${inv.discount_total ? `<div class="row"><span>الخصم</span><span>- ${inv.discount_total.toLocaleString("ar-EG")}</span></div>` : ""}${inv.vat_amount ? `<div class="row"><span>ض.ق.م (${inv.vat_rate}%)</span><span>${inv.vat_amount.toLocaleString("ar-EG")}</span></div>` : ""}<div class="total">الإجمالي:${dual ? `<div>${nf(inv.total*fx)} ${base}</div><div style="font-size:11px;font-weight:normal;color:#333">${nf(inv.total)} ${cur}</div>` : ` ${nf(inv.total)} ${cur}`}</div><div class="row"><span>المدفوع</span><span>${(inv.paid_amount||0).toLocaleString("ar-EG")}</span></div>${inv.remaining ? `<div class="row" style="color:#DC2626;font-weight:bold"><span>المتبقي</span><span>${inv.remaining.toLocaleString("ar-EG")}</span></div>` : ""}`),
      notes: inv.notes ? block("notes", `<div class="notes">${String(inv.notes).replace(/</g,"&lt;")}</div>`) : "",
      terms: invoiceTemplate.payment_terms ? block("terms", `<div class="notes">شروط الدفع: ${invoiceTemplate.payment_terms}</div>`) : "",
      footer: block("footer", invoiceTemplate.footer_text || "شكراً لتعاملكم معنا"),
    };
    const invoiceHtml = blockOrder.filter(id => sections[id] && !hiddenBlocks.includes(id)).map(id => sections[id]).join("");
    // On narrow receipts (58mm), QR is more legible than CODE128 for thermal printers. Use QR here.
    w.document.write(`
      <html dir="rtl"><head><title>فاتورة ${inv.display_no || fmtInvNum(inv.number)}</title>
      <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"></script>
      <script src="https://cdn.jsdelivr.net/npm/qrcode@1.5.3/build/qrcode.min.js"></script>
      <style>
        @page { size: ${layout === "thermal" ? "80mm auto" : "A4"}; margin: ${layout === "thermal" ? "4mm" : `${Math.max(0, Math.min(35, Number(design.pageMargin ?? 12)))}mm`}; }
        body{font-family:Cairo,Arial;font-size:${Math.max(8, Math.min(24, Number(design.fontSize || 12)))}px;width:${layout === "thermal" ? "72mm" : "100%"};margin:0 auto;color:#000;padding:${layout === "thermal" ? "0" : `${Math.max(0, Math.min(35, Number(design.pageMargin ?? 12)))}px`}}
        .designer-block{width:100%;break-inside:avoid}
        body:not(.layout-thermal) h1{font-size:22px} body:not(.layout-thermal) th,body:not(.layout-thermal) td{padding:8px;border:1px solid #333} body:not(.layout-thermal) th{background:#e5e7eb}
        .layout-modern h1{color:#0f766e}.layout-modern th{background:#0f766e!important;color:#fff}.layout-simple h1{color:#2563eb}.layout-simple th{background:#eff6ff!important;color:#1e40af}
        h1{font-size:14px;margin:4px 0;text-align:center}
        table{width:100%;border-collapse:collapse;margin-top:6px}
        th,td{padding:2px 3px;font-size:11px;text-align:start;border-bottom:1px dashed #999}
        .total{font-size:14px;font-weight:bold;text-align:end;margin-top:6px;border-top:2px solid #000;padding-top:4px}
        .row{display:flex;justify-content:space-between;padding:1px 0}
        .center{text-align:center}
        .scan-block{margin-top:8px;text-align:center;border-top:1px dashed #999;padding-top:6px}
        .scan-block .options{display:flex;justify-content:center;gap:8px;align-items:center;margin-bottom:4px;flex-wrap:wrap}
        .scan-block svg{max-width:100%;height:36px}
        .scan-block canvas{margin:0 auto;display:block}
        .toggle-btn{font-size:10px;padding:2px 6px;border:1px solid #ccc;background:#fff;cursor:pointer;border-radius:3px}
        .toggle-btn.active{background:#1E3A8A;color:#fff;border-color:#1E3A8A}
        @media print { .toggle-btn { display:none } .options { display:none } }
      </style></head><body class="layout-${layout}">
      ${invoiceHtml}
      <script>
        const val = ${JSON.stringify(barcodeVal)};
        const zatcaB64 = ${JSON.stringify(zatcaB64 || "")};
        function drawBarcode() {
          try { if (window.JsBarcode) JsBarcode("#inv-barcode", val, { format: "CODE128", displayValue: true, fontSize: 10, height: 28, margin: 2, width: 1.5 }); } catch(e) {}
        }
        function drawQR() {
          try {
            if (window.QRCode) {
              // Use ZATCA payload when present, else fallback to invoice number
              const data = zatcaB64 || val;
              QRCode.toCanvas(document.getElementById("inv-qr"), data, { width: 80, margin: 1 }, function(){});
            }
          } catch(e) {}
        }
        function setMode(m) {
          const bc = document.getElementById("inv-barcode");
          const qr = document.getElementById("inv-qr");
          const bBtn = document.getElementById("mode-barcode");
          const qBtn = document.getElementById("mode-qr");
          if (m === 'qr') { bc.style.display='none'; qr.style.display='block'; bBtn.classList.remove('active'); qBtn.classList.add('active'); drawQR(); }
          else { bc.style.display=''; qr.style.display='none'; qBtn.classList.remove('active'); bBtn.classList.add('active'); }
          try { localStorage.setItem('inv_scan_mode', m); } catch(e) {}
        }
        window.onload = () => {
          function tryDraw() {
            if (window.JsBarcode) drawBarcode();
            if (window.QRCode) drawQR();
            try { const saved = localStorage.getItem('inv_scan_mode'); if (saved === 'qr') setMode('qr'); } catch(e) {}
          }
          if (window.JsBarcode && window.QRCode) {
            tryDraw();
            setTimeout(() => window.print(), 200);
          } else {
            let tries = 0;
            const iv = setInterval(() => {
              tries++;
              if (window.JsBarcode && window.QRCode) {
                clearInterval(iv); tryDraw();
                setTimeout(() => window.print(), 200);
              } else if (tries > 30) {
                clearInterval(iv); tryDraw(); window.print();
              }
            }, 100);
          }
        };
      </script>
      </body></html>
    `);
    w.document.close();
  };

  return (
    <div className="grid lg:grid-cols-12 gap-3 lg:gap-4 min-h-[calc(100dvh-8.75rem)] lg:h-[calc(100vh-8rem)] lg:min-h-0" data-testid={`${kind}-editor`}>
      <div className="lg:hidden grid grid-cols-2 gap-1 rounded-lg border bg-muted/70 p-1" role="tablist" aria-label="شاشة نقطة البيع">
        <button type="button" role="tab" aria-selected={mobilePanel === "products"} onClick={() => setMobilePanel("products")} className={`min-h-11 rounded-md px-3 text-sm font-bold transition-colors ${mobilePanel === "products" ? "bg-card text-primary shadow" : "text-muted-foreground"}`} data-testid="mobile-products-tab"><Search className="inline w-4 h-4 me-1" />الأصناف</button>
        <button type="button" role="tab" aria-selected={mobilePanel === "cart"} onClick={() => setMobilePanel("cart")} className={`min-h-11 rounded-md px-2 text-xs sm:text-sm font-bold transition-colors ${mobilePanel === "cart" ? "bg-card text-primary shadow" : "text-muted-foreground"}`} data-testid="mobile-cart-tab"><ShoppingCart className="inline w-4 h-4 me-1" />الفاتورة ({lines.length}) · {fmtMoney(total, currency)}</button>
      </div>
      {/* Cart - left */}
      <div className={`${mobilePanel === "cart" ? "flex" : "hidden"} lg:flex lg:col-span-5 min-h-[560px] lg:min-h-0 border border-border rounded-xl bg-card flex-col shadow-sm`}>

        <div className="p-4 border-b border-border bg-muted/20">
          <div className="flex items-center gap-2 mb-3">
            {isEdit ? <Pencil className="w-5 h-5 text-primary" /> : <ShoppingCart className="w-5 h-5 text-primary" />}
            <h2 className="font-heading font-bold text-lg">
              {kindTitle(kind, isEdit, existing)}
            </h2>
          </div>
          {returnSource && !isEdit && (
            <div className="mb-3 flex items-center justify-between gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs" data-testid="return-source-banner">
              <div className="flex items-center gap-1.5 text-amber-800">
                <RotateCcw className="w-3.5 h-3.5" />
                قالب "مرتجع كامل" من الفاتورة <b className="font-mono">#{returnSource.number}</b>
              </div>
              <Button type="button" size="sm" variant="outline" onClick={applyFullReturnTemplate} className="h-6 text-[10px] border-amber-400 text-amber-800 hover:bg-amber-100 gap-1" data-testid="apply-full-return-btn">
                استعادة الكميات الأصلية
              </Button>
            </div>
          )}
          {!isEdit && draftSavedAt && (
            <div className="mb-3 flex items-center gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-1.5 text-[11px] text-sky-800" data-testid="invoice-draft-status">
              <Save className="w-3.5 h-3.5" /> مسودة محفوظة تلقائياً · {new Date(draftSavedAt).toLocaleTimeString("ar-EG", { hour: "2-digit", minute: "2-digit" })}
            </div>
          )}
          {isReturnKind(kind) && !isEdit && (
            <div className="mb-3 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-1.5 text-[11px] text-emerald-800" data-testid="return-price-hint">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              الأسعار تُحسب تلقائياً على أساس <b>{isSaleSide(kind) ? "سعر البيع" : "سعر الشراء (التكلفة)"}</b> من كرت الصنف
            </div>
          )}
          <form onSubmit={onScan} className="relative flex gap-2">
            <div className="relative flex-1">
              <ScanBarcode className="absolute start-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary" />
              <Input ref={scanRef} value={barcodeVal} onChange={(e)=>setBarcodeVal(e.target.value)} placeholder={t("inv_scan_ph")} className="ps-10 h-12 text-base sm:text-lg font-mono" data-testid="barcode-scanner-input" />
            </div>
            <Button type="button" variant="outline" size="icon" className="h-11 w-11 shrink-0" onClick={()=>setCamOpen(true)} data-testid="camera-scan-button" title="مسح بالكاميرا">
              <Camera className="w-5 h-5" />
            </Button>
          </form>
          <div className="text-[10px] text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 mt-1" data-testid="shortcuts-hint">
            <span><kbd className="px-1 border rounded bg-muted">F2</kbd> باركود</span>
            <span><kbd className="px-1 border rounded bg-muted">F3</kbd> بحث</span>
            <span><kbd className="px-1 border rounded bg-muted">F9</kbd> حفظ</span>
            <span><kbd className="px-1 border rounded bg-muted">Esc</kbd> إلغاء</span>
            <span className="text-emerald-700">⚡ نقدي · SYP · مدفوع كامل (تلقائي)</span>
            <button type="button" onClick={()=>{
              const muted = localStorage.getItem("mute_chime") === "1";
              localStorage.setItem("mute_chime", muted ? "0" : "1");
              toast.info(muted ? "🔊 صوت التأكيد مُفعّل" : "🔇 صوت التأكيد مكتوم");
            }} className="ms-auto text-muted-foreground hover:text-foreground underline" data-testid="toggle-chime-btn">
              {(localStorage.getItem("mute_chime") === "1") ? "🔇 صوت مكتوم" : "🔊 صوت التأكيد"}
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-2">
          {lines.length === 0 ? (
            <div className="text-center text-muted-foreground py-12 text-sm">{t("inv_no_items")}</div>
          ) : (
            <React.Fragment>
            <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm min-w-[460px]">
              <thead className="text-xs uppercase text-muted-foreground border-b border-border">
                <tr><th className="p-2 text-start">الصنف</th><th className="p-2">الكمية</th><th className="p-2">السعر</th><th className="p-2">الإجمالي</th><th></th></tr>
              </thead>
              <tbody className="text-[13px]">
                {lines.map((l, i) => {
                  const prod = products.find(p => p.id === l.product_id);
                  const needsSerials = prod?.controlled && (kind === "sale" || kind === "sale_return");
                  const serialsFilled = (l.serials || []).filter(Boolean).length;
                  const serialsOk = !needsSerials || serialsFilled === l.quantity;
                  return (
                  <React.Fragment key={i}>
                    <tr className={`border-b border-border ${needsSerials && !serialsOk ? "bg-red-50/40" : ""}`}>
                      <td className="p-2 font-semibold">
                        {l.product_name}
                        {needsSerials && (
                          <span className={`ms-1 text-[9px] px-1 py-0.5 rounded ${serialsOk ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"} font-bold`}>
                            {serialsOk ? "✓ سيريال" : "!سيريال ناقص"}
                          </span>
                        )}
                      </td>
                      <td className="p-2">
                        <div className="flex items-center gap-1 justify-center">
                          <Button size="icon" variant="ghost" className="w-6 h-6" onClick={()=>updateLine(i,{quantity:l.quantity-1})}><Minus className="w-3 h-3" /></Button>
                          <Input className="w-14 h-7 text-center tabular-nums" type="number" value={l.quantity} onChange={(e)=>updateLine(i,{quantity:+e.target.value})} />
                          <Button size="icon" variant="ghost" className="w-6 h-6" onClick={()=>updateLine(i,{quantity:l.quantity+1})}><Plus className="w-3 h-3" /></Button>
                        </div>
                      </td>
                      <td className="p-2"><Input className="w-20 h-7 tabular-nums text-center" type="number" value={l.price} onChange={(e)=>updateLine(i,{price:+e.target.value})} /></td>
                      <td className="p-2 tabular-nums font-bold">{l.total.toLocaleString("ar-EG")}</td>
                      <td className="p-2"><Button size="icon" variant="ghost" className="w-7 h-7 text-destructive" onClick={()=>setLines(ls=>ls.filter((_,x)=>x!==i))}><Trash2 className="w-3.5 h-3.5" /></Button></td>
                    </tr>
                    {needsSerials && (
                      <tr className="border-b border-border bg-amber-50/30">
                        <td colSpan={5} className="p-2">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Label className="text-[10px] text-amber-800 font-bold">أرقام تسلسلية ({serialsFilled}/{l.quantity}):</Label>
                            {Array.from({ length: l.quantity }).map((_, si) => (
                              <Input
                                key={si}
                                value={(l.serials || [])[si] || ""}
                                onChange={(e) => {
                                  const newSerials = [...(l.serials || Array(l.quantity).fill(""))];
                                  newSerials[si] = e.target.value;
                                  updateLine(i, { serials: newSerials });
                                }}
                                placeholder={`SN${si + 1}`}
                                className="h-6 w-24 text-[10px] font-mono"
                                data-testid={`serial-${i}-${si}`}
                              />
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            </div>
            <div className="sm:hidden space-y-2" data-testid="mobile-invoice-lines">
              {lines.map((line, index) => {
                const product = products.find(item => item.id === line.product_id);
                const controlled = product?.controlled && (kind === "sale" || kind === "sale_return");
                return <div key={`${line.product_id || line.product_name}-${index}`} className="rounded-xl border border-border bg-card p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-2"><div className="min-w-0"><div className="font-bold text-base leading-snug break-words">{line.product_name}</div>{controlled && <span className="text-[10px] text-amber-700">أرقام تسلسلية مطلوبة</span>}</div><Button size="icon" variant="ghost" className="h-9 w-9 shrink-0 text-destructive" aria-label={`حذف ${line.product_name}`} onClick={() => setLines(ls => ls.filter((_, i) => i !== index))}><Trash2 className="w-4 h-4" /></Button></div>
                  <div className="grid grid-cols-3 gap-2 mt-3 items-end">
                    <div><Label className="text-xs font-semibold">الكمية</Label><div className="flex items-center gap-1"><Button type="button" size="icon" variant="outline" className="h-10 w-10 shrink-0" aria-label="إنقاص الكمية" onClick={() => updateLine(index, { quantity: line.quantity - 1 })}><Minus className="w-3.5 h-3.5" /></Button><Input className="h-10 min-w-0 px-2 text-center text-base tabular-nums" type="number" value={line.quantity} onChange={e => updateLine(index, { quantity: +e.target.value })} /><Button type="button" size="icon" variant="outline" className="h-10 w-10 shrink-0" aria-label="زيادة الكمية" onClick={() => updateLine(index, { quantity: line.quantity + 1 })}><Plus className="w-3.5 h-3.5" /></Button></div></div>
                    <div><Label className="text-xs font-semibold">السعر</Label><Input className="h-10 min-w-0 px-2 text-center text-base tabular-nums" type="number" value={line.price} onChange={e => updateLine(index, { price: +e.target.value })} /></div>
                    <div className="text-end"><Label className="text-xs font-semibold">الإجمالي</Label><div className="h-9 flex items-center justify-end font-bold tabular-nums text-primary text-xs">{fmtMoney(line.total, currency)}</div></div>
                  </div>
                  {controlled && <div className="mt-2 flex flex-wrap gap-1.5">{Array.from({ length: line.quantity }).map((_, serialIndex) => <Input key={serialIndex} value={(line.serials || [])[serialIndex] || ""} onChange={e => { const serials = [...(line.serials || Array(line.quantity).fill(""))]; serials[serialIndex] = e.target.value; updateLine(index, { serials }); }} placeholder={`SN${serialIndex + 1}`} className="h-8 w-24 text-xs font-mono" />)}</div>}
                </div>;
              })}
            </div>
            </React.Fragment>
          )}
        </div>

        <div className="border-t border-border p-3 sm:p-4 space-y-3 bg-muted/30">
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2 relative">
              <Label className="text-sm">{partyLabel}</Label>
              <Input value={partyName} onChange={(e)=>{setPartyName(e.target.value); setPartyId(null);}} data-testid="party-name-input" />
              {partyName && filteredParties.length > 0 && partyName !== (parties.find(p=>p.id===partyId)?.name) && (
                <div className="absolute z-10 w-full border border-border rounded-md bg-card mt-1 max-h-32 overflow-auto text-xs shadow-md">
                  {filteredParties.map(p => (
                    <button key={p.id} type="button" onClick={()=>{setPartyId(p.id); setPartyName(p.name);}} className="block w-full text-start px-2 py-1.5 hover:bg-accent">{p.name} <span className="text-muted-foreground">{p.phone}</span></button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label className="text-sm">{t("inv_payment")}</Label>
              <Select value={payment} onValueChange={setPayment}>
                <SelectTrigger data-testid="payment-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">نقدي</SelectItem>
                  <SelectItem value="credit">آجل</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">{t("inv_paid_amount")}</Label>
              <Input type="number" value={paidAmount} onChange={(e)=>setPaidAmount(e.target.value)} data-testid="paid-amount-input" placeholder={payment==="cash"?"فارغ = دفع كامل":""} disabled={splits.length > 0} />
            </div>
            {(() => {
              const boxesForCur = cashBoxes.filter(b => b.currency === currency);
              const isCashier = user?.role === "cashier";
              // Cashier with assigned box always sees it (in basic view); otherwise only when advanced is open OR multiple boxes / splits present
              const forceShow = isCashier || splits.length > 0;
              if (boxesForCur.length <= 1 && !forceShow && !showAdvanced) return null;
              return (
                <div className="col-span-2">
                  <Label className="text-xs flex items-center justify-between">
                    <span>الصندوق {isCashier && user?.default_cash_box_id && <span className="text-[9px] px-1 rounded bg-emerald-100 text-emerald-700 ms-1">مخصص لك</span>}</span>
                    <button type="button" onClick={()=>setSplitOpen(true)} className="text-[10px] text-primary hover:underline" data-testid="open-split-btn">
                      {splits.length ? `دفع مقسّم (${splits.length})` : "+ دفع مقسّم"}
                    </button>
                  </Label>
                  {splits.length === 0 ? (
                    <Select value={cashBoxId || "__default__"} onValueChange={(v)=>setCashBoxId(v === "__default__" ? "" : v)}>
                      <SelectTrigger data-testid="cash-box-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__default__">الصندوق الافتراضي</SelectItem>
                        {boxesForCur.map(b => (<SelectItem key={b.id} value={b.id}>{b.name || `صندوق ${b.currency}`}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="text-[10px] text-muted-foreground border border-dashed rounded p-1.5 text-center">دفع مقسّم مفعّل — {splits.length} حركة</div>
                  )}
                </div>
              );
            })()}

            {/* Advanced toggle */}
            <div className="col-span-2 flex items-center gap-2 pt-1">
              <button type="button" onClick={()=>setShowAdvanced(v=>!v)} className="text-[11px] text-muted-foreground hover:text-primary flex items-center gap-1 transition-colors" data-testid="toggle-advanced-btn">
                <span className={`inline-block transition-transform ${showAdvanced ? "rotate-90" : ""}`}>▸</span>
                {showAdvanced ? t("inv_adv_hide") : t("inv_adv_show")}
              </button>
            </div>

            {showAdvanced && <>
              <div>
                <Label className="text-sm">{t("inv_disc_pct")}</Label>
                <Input type="number" value={discountPercent} onChange={(e)=>setDiscountPercent(e.target.value)} data-testid="discount-percent-input" step="0.1" min="0" max="100" />
              </div>
              <div>
                <Label className="text-sm">{t("inv_disc_amt")}</Label>
                <Input type="number" value={discountAmount} onChange={(e)=>setDiscountAmount(e.target.value)} data-testid="discount-amount-input" step="0.01" min="0" />
              </div>
              <div>
                <Label className="text-sm">{t("inv_vat_label")}</Label>
                <Input type="number" value={vatRateOverride} onChange={(e)=>setVatRateOverride(e.target.value)} placeholder={t("inv_vat_ph")} data-testid="vat-rate-input" step="0.1" min="0" max="100" />
              </div>
              <div>
                <Label className="text-sm">{t("inv_warehouse")}</Label>
                <Select value={warehouseId || "__default__"} onValueChange={(v)=>setWarehouseId(v === "__default__" ? "" : v)}>
                  <SelectTrigger data-testid="warehouse-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__default__">الافتراضي</SelectItem>
                    {warehouses.map(w => (<SelectItem key={w.id} value={w.id}>{w.name}{w.code ? ` (${w.code})` : ""}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              {isSaleSide(kind) && costCenters.length > 0 && (
                <div>
                  <Label className="text-sm">مركز التكلفة / الفرع {user?.branch_cost_center && <span className="text-primary">(مثبّت)</span>}</Label>
                  <Select value={costCenter || "__none__"} disabled={!!user?.branch_cost_center} onValueChange={(v)=>{ const name = v === "__none__" ? "" : v; setCostCenter(name); const cc = costCenters.find(c => c.name === name); setWarehouseId(cc?.warehouse_id || ""); }}>
                    <SelectTrigger data-testid="cost-center-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      {costCenters.map(c => (<SelectItem key={c.id} value={c.name}>{c.name}{c.warehouse_id ? " 🏬" : ""}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-sm">{t("currency")}</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {currencyOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-sm">{t("inv_exchange")}</Label><Input type="number" value={rate} onChange={(e)=>setRate(e.target.value)} /></div>
            </>}
            {isReturnKind(kind) && (
              <div className="col-span-2">
                <Label className="text-xs flex items-center gap-1">
                  <RotateCcw className="w-3 h-3 text-amber-600" /> سبب المرتجع
                </Label>
                <Select value={returnReason || "__none__"} onValueChange={(v)=>setReturnReason(v === "__none__" ? "" : v)}>
                  <SelectTrigger data-testid="return-reason-select"><SelectValue placeholder="اختر سبباً (اختياري)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— بدون تحديد —</SelectItem>
                    <SelectItem value="damaged">تالف</SelectItem>
                    <SelectItem value="expired">منتهي الصلاحية</SelectItem>
                    <SelectItem value="not_satisfied">عدم رضا العميل</SelectItem>
                    <SelectItem value="wrong_item">صنف خاطئ</SelectItem>
                    <SelectItem value="other">أخرى</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {kind === "purchase" && (
              <div className="col-span-2">
                <Label className="text-xs flex items-center gap-1">
                  رقم فاتورة المورد <span className="text-[10px] text-muted-foreground">(اختياري — رقم الفاتورة الأصلية من المورد)</span>
                </Label>
                <input
                  type="text"
                  value={supplierInvNumber}
                  onChange={(e)=>setSupplierInvNumber(e.target.value)}
                  data-testid="supplier-invoice-number-input"
                  placeholder="مثال: INV-2026-4521"
                  className="w-full mt-1 p-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 font-mono"
                />
              </div>
            )}
            <div className="col-span-2">
              <Label className="text-sm">{t("inv_notes")}</Label>
              <textarea value={notes} onChange={(e)=>setNotes(e.target.value)} rows={2}
                data-testid="invoice-notes-input"
                placeholder="أي ملاحظات (شروط دفع، طلبات خاصة، تسليم...)"
                className="w-full mt-1 p-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" />
            </div>
          </div>

          <div className="space-y-1 border-t border-border pt-3 text-sm">
            <div className="flex justify-between text-muted-foreground"><span>{t("inv_subtotal")}</span><span className="tabular-nums">{fmtMoney(subtotal, currency)}</span></div>
            {discountTotal > 0 && <div className="flex justify-between text-destructive"><span>{t("discount")}</span><span className="tabular-nums">- {fmtMoney(discountTotal, currency)}</span></div>}
            {effectiveVatRate > 0 && <div className="flex justify-between text-muted-foreground" data-testid="invoice-vat-line"><span>{t("inv_vat_label").replace(" %","")} ({effectiveVatRate}%)</span><span className="tabular-nums">{fmtMoney(vatAmount, currency)}</span></div>}
            <div className="flex justify-between text-lg font-heading font-bold text-primary"><span>{t("inv_final_total")}</span><span className="tabular-nums" data-testid="invoice-total">{fmtMoney(total, currency)}</span></div>
            <div className="flex justify-between text-sm text-emerald-700"><span>{t("inv_paid")}</span><span className="tabular-nums" data-testid="paid-display">{fmtMoney(effectivePaid, currency)}</span></div>
            {remaining !== 0 && (
              <div className={`flex justify-between font-bold ${remaining > 0 ? "text-destructive" : "text-amber-600"}`}>
                <span>{remaining > 0 ? t("inv_remaining_cust") : t("inv_surplus")}</span>
                <span className="tabular-nums" data-testid="remaining-display">{fmtMoney(Math.abs(remaining), currency)}</span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={()=>save(false)} data-testid="save-invoice-button"><Save className="w-4 h-4 me-1" /> {isEdit ? t("inv_save_edit") : t("save")}</Button>
            <Button onClick={()=>save(true)} data-testid="save-print-button"><Printer className="w-4 h-4 me-1" /> {t("inv_save_print")}</Button>
          </div>
        </div>
      </div>

      <ProductPickerPanel
        mobilePanel={mobilePanel}
        kind={kind}
        isEdit={isEdit}
        nav={nav}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        categories={categories}
        activeCat={activeCat}
        setActiveCat={setActiveCat}
        catCounts={catCounts}
        filteredProducts={filteredProducts}
        addLine={addLine}
      />
      <BarcodeScannerDialog open={camOpen} onClose={()=>setCamOpen(false)} onScan={async (code)=>{
        setCamOpen(false);
        try { const { data } = await api.get(`/products/barcode/${code.trim()}`); addLine(data); }
        catch { toast.error("لم يتم العثور على الصنف"); }
      }} />

      <SplitPaymentDialog open={splitOpen} onOpenChange={setSplitOpen} splits={splits} setSplits={setSplits} cashBoxes={cashBoxes} currency={currency} />
    </div>
  );
}
